<template>
  <div>
    <AppTooltip v-if="envelopeCanBeChase">
      <template #activator="{ props }">
        <app-button :loading="isLoading"
                    :disabled="isEnvelopeChaseButtonDisabled"
                    size="small"
                    type="tonal"
                    variant="brand"
                    v-bind="props"
                    @click.stop="prepareChaseEnvelope"
        >
          <span>{{ textChaseButton }}</span>
        </app-button>
      </template>
      <span>{{ chaseEnvelopeTooltip }}</span>
    </AppTooltip>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { TranslateResult } from 'vue-i18n'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'

import AppTooltip from '@/common/AppTooltip.vue'
import { toast } from '@/common/utils/toast'
import { CHASE_USERS, CHASE_VALIDATOR } from '@/store/modules/room/action_types'
import { ADD_CHASE_TIMER_ENVELOPE } from '@/store/modules/room/mutation_types'

export default defineComponent({
  name: 'ProjectSettingsSignaturesEnvelopeChaseButton',
  components: { AppTooltip },
  props: {
    item: {
      type: Object as unknown as any,
      required: true,
    },
    currentDate: {
      type: Object as unknown as any,
      required: true,
    },
  },
  data () {
    return {
      isLoading: false,
    }
  },
  computed: {
    ...mapState('room', ['chaseTimers']),
    ...mapGetters('room', ['roomMnemo']),
    hasValidatorsToChase (): boolean {
      return !this.item.validated && (this.item.status === 'unvalidated' || this.item.status === 'sent')
    },
    hasSignatoriesToChase (): boolean {
      return this.item.signatories.some((signatory: any) => signatory.status === 'sent')
    },
    envelopeCanBeChase (): boolean {
      return this.hasValidatorsToChase || this.hasSignatoriesToChase
    },
    isEnvelopeChaseButtonDisabled (): boolean {
      const envelopeChaseTimer = this.chaseTimers.envelope?.[this.item.id]
      return envelopeChaseTimer && this.currentDate.isBefore(envelopeChaseTimer)
    },
    chaseEnvelopeTooltip (): TranslateResult | null {
      if (this.hasValidatorsToChase && this.hasSignatoriesToChase) {
        return this.$t('project.project-settings.signatures.ProjectSettingsSignaturesEnvelopeChaseButton.chaseValidatorsAndSignatoriesTooltip')
      } else if (this.hasValidatorsToChase) {
        return this.$t('project.project-settings.signatures.ProjectSettingsSignaturesEnvelopeChaseButton.chaseValidatorsTooltip')
      } else if (this.hasSignatoriesToChase) {
        return this.$t('project.project-settings.signatures.ProjectSettingsSignaturesEnvelopeChaseButton.chaseSignatoriesTooltip')
      }
      return null
    },
    textChaseButton (): TranslateResult | null {
      if (this.hasValidatorsToChase && this.hasSignatoriesToChase) {
        return this.$t('project.project-settings.signatures.ProjectSettingsSignaturesEnvelopeChaseButton.chaseValidatorsAndSignatories')
      } else if (this.hasValidatorsToChase) {
        return this.$t('project.project-settings.signatures.ProjectSettingsSignaturesEnvelopeChaseButton.chaseValidators')
      } else if (this.hasSignatoriesToChase) {
        return this.$t('project.project-settings.signatures.ProjectSettingsSignaturesEnvelopeChaseButton.chaseSignatories')
      }
      return null
    },
  },
  methods: {
    ...mapActions('room', [CHASE_USERS, CHASE_VALIDATOR]),
    ...mapMutations('room', [ADD_CHASE_TIMER_ENVELOPE]),
    async prepareChaseEnvelope (): Promise<void> {
      // TODO : Handle pendingIDS with vuex-action
      this.isLoading = true
      try {
        if (this.hasValidatorsToChase) {
          await this.CHASE_VALIDATOR({
            mnemo: this.roomMnemo,
            envelopeId: this.item.id,
            data: [],
          })
        }
        if (this.hasSignatoriesToChase) {
          await this.CHASE_USERS({
            mnemo: this.roomMnemo,
            envelopeId: this.item.id,
          })
        }
        toast.success(this.$t('common.chaseSuccess'))
        this.ADD_CHASE_TIMER_ENVELOPE(this.item.id)
      } catch (error) {
        toast.error(this.$t('project.project-settings.signatures.ProjectSettingsSignaturesEnvelopeChaseButton.chaseEnvelopeError'))
      } finally {
        this.isLoading = false
      }
    },
  },
})
</script>
